import {
  Accordion,
  Button,
  CircularProgress,
  Text,
  TextChip,
} from '@shared/components';
import { AssigneeOptionVariant } from '@shared/components/dropdown-chip/DropdownChip';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import {
  SkillChangeApprovalOutcome,
  useResolveSkillChangeRequestMutation,
  useResolveTrackSettingsChangeRequestMutation,
  useRunSkillChangeTestsMutation,
} from '@shared/generated/graphql';
import { RUN_SKILL_CHANGE_TESTS_VERB } from 'clerk_common/cards/skillChange';
import {
  APPLY_TRACK_SETTINGS_CHANGE_VERB,
  DENY_TRACK_SETTINGS_CHANGE_VERB,
} from 'clerk_common/cards/trackSettingsChange';
import {
  CardAction,
  CardActionStyle,
  CardBlock,
  CardResolution,
  formatCardResolution,
  InteractiveCard as InteractiveCardData,
  InteractiveCardType,
} from 'clerk_common/cards/types';
import { SkillChangeTargetKind } from 'clerk_common/escalations/skillChangeApproval';
import { useState } from 'react';
import { UnifiedDiffView } from './UnifiedDiffView';

type InteractiveCardProps = {
  card: InteractiveCardData;
  onResolved?: () => void;
};

export const InteractiveCard = ({ card, onResolved }: InteractiveCardProps) => {
  const { isVoomaAdmin } = useMeContext();
  const [localResolution, setLocalResolution] = useState<CardResolution>();
  const resolution = card.resolution ?? localResolution;
  const targetKind = card.targetKind ?? SkillChangeTargetKind.ACTION_DEFINITION;
  const showsGlobalPromptScope =
    isVoomaAdmin && targetKind === SkillChangeTargetKind.GLOBAL_PROMPT;
  const isTrackSettingsChange =
    card.cardType === InteractiveCardType.TRACK_SETTINGS_CHANGE_APPROVAL;
  const canResolve =
    (targetKind !== SkillChangeTargetKind.GLOBAL_PROMPT || isVoomaAdmin) &&
    (!isTrackSettingsChange || isVoomaAdmin);

  return (
    <div className="my-1 w-full max-w-xl rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <Text type="body-sm" isHeavy className="mb-2">
        {card.title}
      </Text>
      {showsGlobalPromptScope && (
        <TextChip
          className="mb-2"
          text="Global prompt — affects all organizations"
          variant={AssigneeOptionVariant.WARNING}
          shouldCapitalize={false}
        />
      )}

      <div className="flex flex-col gap-2">
        {card.blocks.map((block, idx) => (
          <CardBlockView key={idx} block={block} />
        ))}
      </div>

      {resolution ? (
        <Text type="body-xs" className="mt-3 text-gray-500">
          {formatCardResolution(resolution)}
        </Text>
      ) : canResolve ? (
        <CardActions
          actions={card.actions}
          onResolved={(outcome) => {
            setLocalResolution({ outcome: displayOutcome(outcome), at: '' });
            onResolved?.();
          }}
        />
      ) : (
        <Text type="body-xs" className="mt-3 text-gray-500">
          Waiting for approval.
        </Text>
      )}
    </div>
  );
};

function CardBlockView({ block }: { block: CardBlock }) {
  switch (block.type) {
    case 'text':
    case 'markdown':
      return (
        <Text type="body-xs" className="whitespace-pre-wrap text-gray-700">
          {block.text}
        </Text>
      );
    case 'facts':
      return (
        <div className="flex flex-col gap-1">
          {block.facts.map((fact) => (
            <div key={fact.label} className="flex gap-2 text-xs">
              <span className="font-medium text-gray-500">{fact.label}</span>
              <span className="text-gray-700">{fact.value}</span>
            </div>
          ))}
        </div>
      );
    case 'diff':
      return (
        <Accordion
          triggerClassName="flex w-full items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600"
          sections={[
            {
              trigger: <span>{block.label}</span>,
              children: (
                <div className="pt-2">
                  <UnifiedDiffView diff={block.text} hunks={block.hunks} />
                </div>
              ),
            },
          ]}
        />
      );
  }
}

function toOutcome(value?: string): SkillChangeApprovalOutcome | undefined {
  if (value === SkillChangeApprovalOutcome.APPLIED) {
    return SkillChangeApprovalOutcome.APPLIED;
  }
  if (value === SkillChangeApprovalOutcome.DENIED) {
    return SkillChangeApprovalOutcome.DENIED;
  }
  return undefined;
}

function toButtonVariant(style?: CardActionStyle) {
  switch (style) {
    case 'danger':
      return 'destructive' as const;
    case 'default':
      return 'secondary' as const;
    default:
      return 'primary' as const;
  }
}

function displayOutcome(outcome: SkillChangeApprovalOutcome): string {
  return outcome === SkillChangeApprovalOutcome.APPLIED ? 'Applied' : 'Denied';
}

function CardActions({
  actions,
  onResolved,
}: {
  actions: CardAction[];
  onResolved: (outcome: SkillChangeApprovalOutcome) => void;
}) {
  const { sendToast } = useToast();
  const [pendingVerb, setPendingVerb] = useState<string | null>(null);
  const [resolveSkillChangeRequest, { loading: resolving }] =
    useResolveSkillChangeRequestMutation();
  const [runSkillChangeTests, { loading: runningTests }] =
    useRunSkillChangeTestsMutation();
  const [
    resolveTrackSettingsChangeRequest,
    { loading: resolvingTrackSettings },
  ] = useResolveTrackSettingsChangeRequestMutation();
  const loading = resolving || runningTests || resolvingTrackSettings;

  const handleRunTests = async (exceptionId: string) => {
    try {
      await runSkillChangeTests({
        variables: { exceptionId },
        refetchQueries: ['GetConversationExternalMessages', 'getExceptionById'],
      });
      sendToast('Running tests on the proposed change.', {
        variant: 'success',
        description: 'Results will be posted to this chat.',
      });
    } catch (error) {
      sendToast('Could not start the tests.', {
        variant: 'error',
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handleResolve = async (
    exceptionId: string,
    outcome: SkillChangeApprovalOutcome,
    verb: string
  ) => {
    const resolve =
      verb === APPLY_TRACK_SETTINGS_CHANGE_VERB ||
      verb === DENY_TRACK_SETTINGS_CHANGE_VERB
        ? resolveTrackSettingsChangeRequest
        : resolveSkillChangeRequest;
    try {
      await resolve({
        variables: { exceptionId, outcome },
        refetchQueries: ['GetConversationExternalMessages'],
        awaitRefetchQueries: true,
      });
      onResolved(outcome);
    } catch (error) {
      sendToast('Could not complete the request.', {
        variant: 'error',
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  const handlePress = async (action: CardAction) => {
    const exceptionId = action.data?.exceptionId;
    if (!exceptionId) {
      sendToast('Unable to process this action.', { variant: 'error' });
      return;
    }

    setPendingVerb(action.verb);
    try {
      if (action.verb === RUN_SKILL_CHANGE_TESTS_VERB) {
        await handleRunTests(exceptionId);
        return;
      }

      const outcome = toOutcome(action.data?.outcome);
      if (!outcome) {
        sendToast('Unable to process this action.', { variant: 'error' });
        return;
      }
      await handleResolve(exceptionId, outcome, action.verb);
    } finally {
      setPendingVerb(null);
    }
  };

  return (
    <div className="mt-3 flex gap-2">
      {actions.map((action) => (
        <Button
          key={action.verb}
          size="small"
          variant={
            action.isInProgress ? 'secondary' : toButtonVariant(action.style)
          }
          isDisabled={loading || action.isInProgress}
          loadingIndicator={
            action.isInProgress ? (
              <CircularProgress size="2xs" className="mr-1.5" />
            ) : undefined
          }
          onPress={() => handlePress(action)}
        >
          {pendingVerb === action.verb ? 'Working…' : action.label}
        </Button>
      ))}
    </div>
  );
}
